/**
 * DynamicTable Component
 *
 * A feature-rich data table component supporting up to 50 rows without virtualization.
 * Includes sorting, filtering, editing, and selection capabilities with full theme integration.
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
  TextField,
  Skeleton,
  Typography,
  Box,
  IconButton,
  useTheme
} from '@mui/material';
import {
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material/styles';

// Type definitions
export type TableColumnAlignment = 'left' | 'center' | 'right';
export type TableVariant = 'default' | 'compact' | 'striped' | 'bordered';
export type TableSize = 'small' | 'medium' | 'large';

export interface TableColumn<TRow extends Record<string, any> = Record<string, any>> {
  key: keyof TRow;
  label: string;
  width?: string | number;
  minWidth?: number;
  maxWidth?: number;
  align?: TableColumnAlignment;
  sortable?: boolean;
  filterable?: boolean;
  editable?: boolean;
  resizable?: boolean;
  format?: (value: any, row: TRow) => React.ReactNode;
  filterType?: 'text' | 'select' | 'number' | 'date' | 'boolean';
  editType?: 'text' | 'select' | 'number' | 'date' | 'boolean';
  headerSx?: SxProps<Theme>;
  cellSx?: SxProps<Theme>;
  validate?: (value: any, row: TRow) => boolean | string;
  /** Optional custom header renderer (overrides label if provided) */
  renderHeader?: () => React.ReactNode;
}

export interface DynamicTableProps<TRow extends Record<string, any> = Record<string, any>> {
  // Data configuration
  data: TRow[];
  columns: TableColumn<TRow>[];
  keyField?: keyof TRow;

  // Performance constraints
  maxRows?: number; // Default: 50 (no virtualization)
  virtualizeAfter?: number;

  // Table features
  sortable?: boolean;
  filterable?: boolean;
  paginated?: boolean;
  pageSize?: number;
  selectable?: boolean;
  editable?: boolean;

  // Theme integration (REQUIRED)
  sx?: SxProps<Theme>;
  variant?: TableVariant;
  size?: TableSize;

  // Event handlers
  onRowClick?: (row: TRow, index: number) => void;
  onRowSelect?: (selectedRows: TRow[]) => void;
  onSort?: (column: keyof TRow, direction: 'asc' | 'desc') => void;
  onFilter?: (filters: Record<string, any>) => void;
  onEdit?: (row: TRow, field: keyof TRow, value: any) => void;

  // Loading state
  loading?: boolean;
  loadingRows?: number;
  skeleton?: boolean;
}

interface SortState {
  column: string | null;
  direction: 'asc' | 'desc';
}

interface FilterState {
  [key: string]: string;
}

interface EditState {
  rowIndex: number | null;
  column: string | null;
  value: any;
}

export const DynamicTable = React.memo((
  props: DynamicTableProps<any>
) => {
  const {
    data,
    columns,
    keyField = 'id',
    maxRows = 50,
    sortable = false,
    filterable = false,
    selectable = false,
    editable = false,
    variant = 'default',
    size = 'medium',
    loading = false,
    loadingRows = 5,
    onRowClick,
    onRowSelect,
    onSort,
    onFilter,
    onEdit,
    sx,
    ...otherProps
  } = props;

  const theme = useTheme();

  // Internal state
  const [sortState, setSortState] = useState<SortState>({ column: null, direction: 'asc' });
  const [filterState, setFilterState] = useState<FilterState>({});
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [editState, setEditState] = useState<EditState>({ rowIndex: null, column: null, value: null });

  // Memoized styles using theme tokens
  const tableStyles = useMemo(() => ({
    container: {
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      overflow: 'auto',
      maxHeight: '70vh',
      ...sx
    },
    table: {
      minWidth: 650,
      ...(variant === 'compact' && {
        '& .MuiTableCell-root': {
          padding: theme.spacing(0.75),
          fontSize: theme.typography.body2.fontSize,
        }
      }),
      ...(variant === 'striped' && {
        '& .MuiTableRow-root:nth-of-type(odd)': {
          backgroundColor: theme.palette.action.hover,
        }
      }),
      ...(variant === 'bordered' && {
        '& .MuiTableCell-root': {
          border: `1px solid ${theme.palette.divider}`,
        }
      })
    },
    headerCell: {
      backgroundColor: theme.palette.background.paper,
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.palette.text.primary,
      borderBottom: `2px solid ${theme.palette.divider}`,
      padding: theme.spacing(size === 'small' ? 1 : size === 'large' ? 2 : 1.5),
    },
    cell: {
      padding: theme.spacing(size === 'small' ? 1 : size === 'large' ? 2 : 1.5),
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    selectedRow: {
      backgroundColor: theme.palette.action.selected,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      }
    },
    editableCell: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      }
    },
    filterInput: {
      margin: 0,
      '& .MuiInputBase-input': {
        padding: theme.spacing(0.5),
        fontSize: theme.typography.body2.fontSize,
      }
    },
    skeleton: {
      backgroundColor: theme.palette.action.hover,
      borderRadius: theme.shape.borderRadius,
    }
  }), [theme, variant, size, sx]);

  // Filter data based on current filters
  const filteredData = useMemo(() => {
    if (!filterable || Object.keys(filterState).length === 0) {
      return data;
    }

    return data.filter(row => {
      return Object.entries(filterState).every(([column, filterValue]) => {
        if (!filterValue) return true;
        const cellValue = String(row[column] || '').toLowerCase();
        return cellValue.includes(filterValue.toLowerCase());
      });
    });
  }, [data, filterState, filterable]);

  // Sort data based on current sort state
  const sortedData = useMemo(() => {
    if (!sortable || !sortState.column) {
      return filteredData;
    }

    const sorted = [...filteredData].sort((a, b) => {
      const aValue = a[sortState.column!];
      const bValue = b[sortState.column!];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortState.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (sortState.direction === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });

    return sorted;
  }, [filteredData, sortState, sortable]);

  // Limit data to maxRows for performance
  const displayData = useMemo(() => {
    return sortedData.slice(0, maxRows);
  }, [sortedData, maxRows]);

  // Handle sort column click
  const handleSort = useCallback((column: keyof any) => {
    if (!sortable) return;

    const newDirection =
      sortState.column === column && sortState.direction === 'asc' ? 'desc' : 'asc';

    const newSortState: SortState = { column: String(column), direction: newDirection };
    setSortState(newSortState);
    onSort?.(column, newDirection);
  }, [sortable, sortState, onSort]);

  // Handle filter change
  const handleFilterChange = useCallback((column: string, value: string) => {
    const newFilters = { ...filterState, [column]: value };
    setFilterState(newFilters);
    onFilter?.(newFilters);
  }, [filterState, onFilter]);

  // Handle row selection
  const handleRowSelect = useCallback((rowIndex: number, checked: boolean) => {
    const newSelectedRows = new Set(selectedRows);

    if (checked) {
      newSelectedRows.add(rowIndex);
    } else {
      newSelectedRows.delete(rowIndex);
    }

    setSelectedRows(newSelectedRows);

    const selectedData = Array.from(newSelectedRows).map(index => displayData[index]);
    onRowSelect?.(selectedData);
  }, [selectedRows, displayData, onRowSelect]);

  // Handle select all
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      const allIndices = displayData.map((_, index) => index);
      setSelectedRows(new Set(allIndices));
      onRowSelect?.(displayData);
    } else {
      setSelectedRows(new Set());
      onRowSelect?.([]);
    }
  }, [displayData, onRowSelect]);

  // Handle cell edit
  const handleCellEdit = useCallback((rowIndex: number, column: string, currentValue: any) => {
    if (!editable) return;

    setEditState({
      rowIndex,
      column,
      value: currentValue
    });
  }, [editable]);

  // Handle edit save
  const handleEditSave = useCallback(() => {
    if (editState.rowIndex !== null && editState.column !== null) {
      const row = displayData[editState.rowIndex];
      onEdit?.(row, editState.column as keyof any, editState.value);
      setEditState({ rowIndex: null, column: null, value: null });
    }
  }, [editState, displayData, onEdit]);

  // Handle edit cancel
  const handleEditCancel = useCallback(() => {
    setEditState({ rowIndex: null, column: null, value: null });
  }, []);

  // Render loading skeleton
  const renderLoadingSkeleton = () => (
    <TableContainer sx={tableStyles.container} data-testid="table-skeleton">
      <Table sx={tableStyles.table} className={`DynamicTable-${variant}`}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={String(column.key)} sx={tableStyles.headerCell}>
                <Skeleton width="80%" height={24} sx={tableStyles.skeleton} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: loadingRows }).map((_, index) => (
            <TableRow key={index} data-testid="skeleton-row">
              {columns.map((column) => (
                <TableCell key={String(column.key)} sx={tableStyles.cell}>
                  <Skeleton width="90%" height={20} sx={tableStyles.skeleton} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Render filter row
  const renderFilterRow = () => {
    if (!filterable) return null;

    return (
      <TableRow>
        {selectable && <TableCell sx={tableStyles.headerCell} />}
        {columns.map((column) => (
          <TableCell key={String(column.key)} sx={tableStyles.headerCell}>
            {column.filterable && (
              <TextField
                size="small"
                variant="outlined"
                placeholder={`Filter ${column.label}...`}
                value={filterState[String(column.key)] || ''}
                onChange={(e) => handleFilterChange(String(column.key), e.target.value)}
                sx={tableStyles.filterInput}
                fullWidth
              />
            )}
          </TableCell>
        ))}
        {editable && <TableCell sx={tableStyles.headerCell} />}
      </TableRow>
    );
  };

  // Render cell content
  const renderCellContent = (row: any, column: TableColumn<any>, rowIndex: number) => {
    const cellValue = row[column.key];
    const isEditing = editState.rowIndex === rowIndex && editState.column === String(column.key);

    if (isEditing) {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            size="small"
            value={editState.value}
            onChange={(e) => setEditState(prev => ({ ...prev, value: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleEditSave();
              if (e.key === 'Escape') handleEditCancel();
            }}
            autoFocus
          />
          <IconButton size="small" onClick={handleEditSave} color="primary">
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleEditCancel}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    }

    const formattedValue = column.format ? column.format(cellValue, row) : cellValue;

    if (editable && column.editable) {
      return (
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          sx={tableStyles.editableCell}
          onDoubleClick={() => handleCellEdit(rowIndex, String(column.key), cellValue)}
        >
          <Box flex={1}>{formattedValue}</Box>
          <IconButton
            size="small"
            onClick={() => handleCellEdit(rowIndex, String(column.key), cellValue)}
            sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    }

    return formattedValue;
  };

  // Show loading skeleton
  if (loading) {
    return renderLoadingSkeleton();
  }

  // Show empty state
  if (displayData.length === 0) {
    return (
      <TableContainer sx={tableStyles.container}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
          flexDirection="column"
          gap={2}
        >
          <Typography variant="h6" color="text.secondary">
            No Data Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data.length === 0 ? 'No data to display' : 'No rows match current filters'}
          </Typography>
        </Box>
      </TableContainer>
    );
  }

  const allSelected = selectedRows.size === displayData.length && displayData.length > 0;
  const someSelected = selectedRows.size > 0 && selectedRows.size < displayData.length;

  return (
    <TableContainer sx={tableStyles.container} {...otherProps}>
      <Table sx={tableStyles.table} className={`DynamicTable-${variant}`}>
        <TableHead>
          <TableRow>
            {selectable && (
              <TableCell
                sx={{
                  ...tableStyles.headerCell,
                  width: 48,
                  padding: theme.spacing(0, 1),
                  textAlign: 'center',
                }}
                padding="checkbox"
              >
                <Checkbox
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  color="primary"
                  size="small"
                />
              </TableCell>
            )}
            {columns.map((column) => (
              <TableCell
                key={String(column.key)}
                align={column.align || 'left'}
                sx={{
                  ...tableStyles.headerCell,
                  width: column.width,
                  minWidth: column.minWidth,
                  maxWidth: column.maxWidth,
                  ...column.headerSx
                }}
                role="columnheader"
              >
                {sortable && column.sortable ? (
                  <TableSortLabel
                    active={sortState.column === String(column.key)}
                    direction={sortState.column === String(column.key) ? sortState.direction : 'asc'}
                    onClick={() => handleSort(column.key)}
                    sx={{ color: 'inherit' }}
                  >
                    {column.renderHeader ? column.renderHeader() : column.label}
                  </TableSortLabel>
                ) : (
                  column.renderHeader ? column.renderHeader() : column.label
                )}
              </TableCell>
            ))}
            {editable && (
              <TableCell sx={tableStyles.headerCell} width={60}>
                Actions
              </TableCell>
            )}
          </TableRow>
          {renderFilterRow()}
        </TableHead>
        <TableBody>
          {displayData.map((row, index) => {
            const isSelected = selectedRows.has(index);
            const rowKey = row[keyField] ?? index;

            return (
              <TableRow
                key={String(rowKey)}
                hover={!!onRowClick}
                selected={isSelected}
                onClick={() => onRowClick?.(row, index)}
                sx={isSelected ? tableStyles.selectedRow : undefined}
                style={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {selectable && (
                  <TableCell
                    sx={{
                      ...tableStyles.cell,
                      width: 48,
                      padding: theme.spacing(0, 1),
                      textAlign: 'center',
                    }}
                    padding="checkbox"
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) => handleRowSelect(index, e.target.checked)}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                )}
                {columns.map((column) => (
                  <TableCell
                    key={String(column.key)}
                    align={column.align || 'left'}
                    sx={{
                      ...tableStyles.cell,
                      ...column.cellSx
                    }}
                  >
                    {renderCellContent(row, column, index)}
                  </TableCell>
                ))}
                {editable && (
                  <TableCell sx={tableStyles.cell}>
                    {/* Additional edit actions could go here */}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo optimization
  return (
    prevProps.data === nextProps.data &&
    prevProps.columns === nextProps.columns &&
    prevProps.loading === nextProps.loading &&
    prevProps.variant === nextProps.variant &&
    prevProps.sortable === nextProps.sortable &&
    prevProps.filterable === nextProps.filterable &&
    prevProps.selectable === nextProps.selectable &&
    prevProps.editable === nextProps.editable
  );
});

DynamicTable.displayName = 'DynamicTable';